import type { MetricRecordData } from "@/types/metrics/contracts"

import type { MetricRecord } from "../../runtime"
import { convertResponseToMetricRecord } from "./createMetricInstance"

interface MetricNameRecord {
  id?: string | null
  metric_name?: string | null
}

interface MetricRecordInstanceInput {
  id?: string | null
  metric_name: string
  metric_display_name: string
  metric_metadata: MetricRecordData["metric_metadata"]
  description: string
  metric_manager: string
  locked: boolean
  category?: string | null
  created_at?: string
  organization_id?: string | null
  organization_slug?: string
  organization_display_name?: string
  created_by?: string | null
  last_updated_at?: string
  last_updated_by?: string | null
  active?: boolean
  output_type?: string
  organization_threshold?: MetricRecordData["organization_threshold"]
}

type MetricLookupInput<T> = Record<string, T | null | undefined> | undefined

export function buildMetricRecordsByKey(
  metricRecordData: Record<string, unknown> | undefined,
): Record<string, MetricRecord> {
  if (!metricRecordData) {
    return {}
  }

  const metricRecords: Record<string, MetricRecord> = {}
  for (const [key, metric] of Object.entries(metricRecordData)) {
    metricRecords[key] = convertResponseToMetricRecord(metric)
  }

  return metricRecords
}

export function buildMetricRecordsByName(
  metricRecordData: Record<string, unknown> | undefined,
): Record<string, MetricRecord> {
  if (!metricRecordData) {
    return {}
  }

  const metricRecords: Record<string, MetricRecord> = {}
  for (const metric of Object.values(metricRecordData)) {
    const record = convertResponseToMetricRecord(metric)
    if (record.metric_name) {
      metricRecords[record.metric_name] = record
    }
  }

  return metricRecords
}

export function buildMetricRecordIndexById<
  T extends {
    id?: string | null
  },
>(metricRecords: MetricLookupInput<T>): Record<string, T> {
  const metricRecordsById: Record<string, T> = {}

  for (const record of Object.values(metricRecords || {})) {
    if (record?.id) {
      metricRecordsById[record.id] = record
    }
  }

  return metricRecordsById
}

export function buildMetricNameToIdLookup<T extends MetricNameRecord>(
  metricRecords: MetricLookupInput<T>,
): Record<string, string> {
  const metricNameToId: Record<string, string> = {}

  for (const record of Object.values(metricRecords || {})) {
    if (record?.id && record.metric_name) {
      metricNameToId[record.metric_name] = record.id
    }
  }

  return metricNameToId
}

export function buildMetricIdToNameLookup<T extends MetricNameRecord>(
  metricRecords: MetricLookupInput<T>,
): Record<string, string> {
  const metricIdToName: Record<string, string> = {}

  for (const record of Object.values(metricRecords || {})) {
    if (record?.id && record.metric_name) {
      metricIdToName[record.id] = record.metric_name
    }
  }

  return metricIdToName
}

export function buildMetricNameIdLookups<T extends MetricNameRecord>(
  metricRecords: MetricLookupInput<T>,
): {
  metricIdToName: Record<string, string>
  metricNameToId: Record<string, string>
} {
  return {
    metricIdToName: buildMetricIdToNameLookup(metricRecords),
    metricNameToId: buildMetricNameToIdLookup(metricRecords),
  }
}

export function buildMetricRecordInstancesById(
  metricRecords: MetricLookupInput<MetricRecordInstanceInput>,
): Record<string, MetricRecord> {
  const metricRecordInstances: Record<string, MetricRecord> = {}

  for (const record of Object.values(metricRecords || {})) {
    if (!record) {
      continue
    }

    const instance = convertResponseToMetricRecord(record)
    if (instance.id) {
      metricRecordInstances[instance.id] = instance
    }
  }

  return metricRecordInstances
}
